/** reCAPTCHA Enterprise — minted in the donor's browser, validated by
 *  wegive-api server-side, used to gate `wfs_…` fraud-session mints.
 *
 *  The SDK loads Google's `enterprise.js` itself the same way Stripe.js
 *  injects its fraud-detection scripts: integrators ship one tag
 *  (`wegive-pay.iife.js`) and never have to know reCAPTCHA exists.
 *
 *  Two server-owned constants make this safe:
 *    • `RECAPTCHA_ACTION` is fixed (`payframesSession`). Integrators cannot
 *      override the action via SDK config or request body — wegive-api
 *      rejects tokens minted under any other action.
 *    • `DEFAULT_SITE_KEY` belongs to WeGive's Google Cloud project. The
 *      hostname allowlist that gates assessments is server-side, scoped to
 *      each org's `domain_aliases`. */

const DEFAULT_SITE_KEY = '6LcGDNUrAAAAAMNPPG2hXSYT7dg81GtaKMNLy3YT'
const RECAPTCHA_ACTION = 'payframesSession'
const DEFAULT_TIMEOUT_MS = 30_000

interface GrecaptchaEnterprise {
  ready(cb: () => void): void
  execute(siteKey: string, opts: { action: string }): Promise<string>
}

interface GrecaptchaGlobal {
  enterprise?: GrecaptchaEnterprise
}

interface LoadOptions {
  /** Override the reCAPTCHA Enterprise script load timeout. Defaults to 30s. */
  timeoutMs?: number
}

const loaders = new Map<string, Promise<GrecaptchaEnterprise | undefined>>()

function getGrecaptchaEnterprise(): GrecaptchaEnterprise | undefined {
  return (globalThis as unknown as { grecaptcha?: GrecaptchaGlobal }).grecaptcha?.enterprise
}

/** Inject Google's `enterprise.js` (idempotent), wait for the global,
 *  then return the reCAPTCHA Enterprise handle. Resolves to `undefined`
 *  on timeout, network failure, CSP rejection, or SSR. */
export function ensureRecaptchaLoaded(
  siteKey: string = DEFAULT_SITE_KEY,
  { timeoutMs = DEFAULT_TIMEOUT_MS }: LoadOptions = {},
): Promise<GrecaptchaEnterprise | undefined> {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return Promise.resolve(undefined)
  }

  const ready = getGrecaptchaEnterprise()
  if (ready) return Promise.resolve(ready)

  const cached = loaders.get(siteKey)
  if (cached) return cached

  const promise = loadRecaptcha(siteKey, timeoutMs)
  loaders.set(siteKey, promise)

  // Don't poison the cache on failure — let a later call retry.
  promise.then((result) => {
    if (!result) loaders.delete(siteKey)
  })

  return promise
}

function loadRecaptcha(siteKey: string, timeoutMs: number): Promise<GrecaptchaEnterprise | undefined> {
  return new Promise((resolve) => {
    let settled = false
    let pollHandle: ReturnType<typeof setInterval> | null = null

    const timer = setTimeout(() => finish(undefined), timeoutMs)

    const finish = (enterprise: GrecaptchaEnterprise | undefined) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (pollHandle) clearInterval(pollHandle)
      if (!enterprise) {
        resolve(undefined)
        return
      }
      enterprise.ready(() => resolve(enterprise))
    }

    // If a script tag is already present (integrator added it manually, or
    // a concurrent SDK invocation injected it), don't add another — wait
    // for the global to appear.
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-wegive-pay-recaptcha], script[src*="/recaptcha/enterprise.js"]',
    )
    if (existing) {
      pollHandle = setInterval(() => {
        const enterprise = getGrecaptchaEnterprise()
        if (enterprise) finish(enterprise)
      }, 50)
      return
    }

    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.defer = true
    script.dataset.wegivePayRecaptcha = siteKey
    script.onload = () => finish(getGrecaptchaEnterprise())
    script.onerror = () => finish(undefined)
    document.head.appendChild(script)
  })
}

/** Generate a fresh Enterprise reCAPTCHA token for a `payframesSession`
 *  mint. The action is hard-coded — server-owned, not integrator-overridable.
 *  Returns `undefined` if the script can't load or execution fails; in that
 *  case the SDK omits the token from the session payload and lets wegive-api
 *  enforce. We never fabricate or trust a token client-side. */
export async function generatePayframesRecaptchaToken(
  siteKey: string = DEFAULT_SITE_KEY,
  options: LoadOptions = {},
): Promise<string | undefined> {
  const enterprise = await ensureRecaptchaLoaded(siteKey, options)
  if (!enterprise) return undefined

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS

  return new Promise<string | undefined>((resolve) => {
    let settled = false
    const finish = (value: string | undefined) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => finish(undefined), timeoutMs)

    try {
      enterprise.ready(async () => {
        try {
          const token = await enterprise.execute(siteKey, { action: RECAPTCHA_ACTION })
          finish(typeof token === 'string' && token !== '' ? token : undefined)
        } catch {
          finish(undefined)
        }
      })
    } catch {
      finish(undefined)
    }
  })
}

/** Test-only escape hatch — clears the in-memory loader cache so each
 *  test starts from a clean slate. Not part of the public API. */
export function __resetRecaptchaCacheForTests(): void {
  loaders.clear()
}
